import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { generateAPI6DPdf } from "../utils/Generateapi6d";
import "./API6DForm.css";

const defaultHeaderData = {
  tcds_no: "",
  section_number: "",
  date: "",
  cr_date: "",
  mark_dept: "",
  customer: "",
  project_name: "",
  offer_no: "",
  offer_date: "",
  po_no: "",
  po_date: "",
  po_received_on: "",
  tcds_released_on: ""
};

function API6DForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [headerData, setHeaderData] = useState(defaultHeaderData);
  const [sections, setSections] = useState([]);
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });
  const [expandedSections, setExpandedSections] = useState({});

  const editMode = !!id;

  useEffect(() => {
    const initializeForm = async () => {
      await fetchSections();
      if (editMode) {
        await loadTCDS();
      }
    };
    initializeForm();
  }, [id]);

  const fetchSections = async () => {
    try {
      setSectionsLoading(true);
      const response = await axios.get("/tcds-sections");
      if (response.data.success) {
        setSections(response.data.data);
        if (!editMode) {
          const initialData = {};
          response.data.data.forEach(section => {
            initialData[section.id] = {};
          });
          setSectionData(initialData);
        }
      }
    } catch (error) {
      console.error("Failed to fetch sections:", error);
      showNotification("error", "Failed to load form sections");
    } finally {
      setSectionsLoading(false);
    }
  };

  const loadTCDS = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/tcds/api-6d/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setHeaderData({
          tcds_no: data.tcds_no || "",
          section_number: data.section_number || "",
          date: data.date || "",
          cr_date: data.cr_date || "",
          mark_dept: data.mark_dept || "",
          customer: data.customer || "",
          project_name: data.project_name || "",
          offer_no: data.offer_no || "",
          offer_date: data.offer_date || "",
          po_no: data.po_no || "",
          po_date: data.po_date || "",
          po_received_on: data.po_received_on || "",
          tcds_released_on: data.tcds_released_on || ""
        });
        if (data.section_data) {
          setSectionData(prev => ({ ...prev, ...data.section_data }));
        }
      }
    } catch (error) {
      showNotification("error", error.response?.data?.message || "Failed to load TCDS");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: "", message: "" }), 3000);
  };

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeaderData(prev => ({ ...prev, [name]: value }));
  };

  const handleSectionDataChange = (sectionId, paramName, value) => {
    setSectionData(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], [paramName]: value }
    }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // ── Shared helper: flatten sectionData ──────────────────────────────────
  const getFlattenedData = () => {
    const flat = {};
    Object.values(sectionData).forEach(section => {
      if (section && typeof section === "object") {
        Object.entries(section).forEach(([key, value]) => {
          flat[key] = value;
        });
      }
    });
    return { ...headerData, ...flat };
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();

    if (!headerData.tcds_no || !headerData.customer || !headerData.po_no) {
      showNotification("error", "Please fill in required fields: TCDS No., Customer, and P.O. No.");
      return;
    }

    try {
      setLoading(true);
      const dataToSubmit = {
        ...headerData,
        tcds_type: "API 6D",
        section_number: headerData.section_number || null,
        section_data: sectionData,
        status: saveAsDraft ? "draft" : "submitted",
        unit_id: location.state?.unitId || null,
      };

      let response;
      if (editMode) {
        response = await axios.put(`/tcds/api-6d/${id}`, dataToSubmit);
      } else {
        response = await axios.post("/tcds/api-6d", dataToSubmit);
      }

      showNotification("success", response.data.message || "TCDS saved successfully");
      setTimeout(() => navigate("/manage-tcds"), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      showNotification("error", error.response?.data?.message || "Failed to save TCDS");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!headerData.tcds_no || !headerData.customer || !headerData.po_no) {
      showNotification("error", "Please fill in required fields before generating PDF");
      return;
    }
    try {
      setLoading(true);
      generateAPI6DPdf(getFlattenedData());
      showNotification("success", "PDF generated successfully");
    } catch (error) {
      console.error("Generate PDF error:", error);
      showNotification("error", error.message || "Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  // ── NEW: Excel download handler ──────────────────────────────────────────
  const handleGenerateExcel = async () => {
    if (!headerData.tcds_no || !headerData.customer || !headerData.po_no) {
      showNotification("error", "Please fill in required fields before generating Excel");
      return;
    }
    try {
      setLoading(true);

      const response = await axios.post("/excel6d/download-api6d", getFlattenedData(), {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `TCDS_API6D_${headerData.tcds_no || "export"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification("success", "Excel generated successfully");
    } catch (error) {
      console.error("Generate Excel error:", error);
      showNotification(
        "error",
        error.response?.data?.message || error.message || "Failed to generate Excel"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="api6d-form-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === "success" ? "✓" : "✕"}
          </div>
          <div className="notification-text">{notification.message}</div>
        </div>
      )}

      {/* Page Header */}
      <div className="api6d-page-header">
        <div>
          <h1 className="api6d-page-title">
            {editMode ? "Edit API 6D TCDS" : "Create API 6D TCDS"}
          </h1>
          <p className="api6d-page-subtitle">Swing Check Valve Contract Review Sheet</p>
        </div>
        <div className="api6d-page-actions">
          <button type="button" className="btn-back" onClick={() => navigate("/manage-tcds")}>
            ← Back
          </button>
        </div>
      </div>

      <form className="api6d-form" onSubmit={(e) => handleSubmit(e, false)}>
        {/* ══════════════════════════════════════════
            HEADER SECTION
            ══════════════════════════════════════════ */}
        <div className="api6d-header-section">
          <div className="header-title-bar">
            <span className="header-title-text">API 6D - SWING CHECK VALVE CONTRACT REVIEW SHEET</span>
            <span className="header-badge">Check Valves Only</span>
          </div>

          {/* Row 1: TCDS No, Section No, Date, CR Date, Mark Dept */}
          <div className="header-row four-column">
            <div className="header-field">
              <label className="header-label">TCDS NO. <span className="required-mark">*</span></label>
              <input type="text" className="header-input" name="tcds_no"
                value={headerData.tcds_no} onChange={handleHeaderChange}
                placeholder="Enter TCDS No." required />
            </div>
            <div className="header-field">
              <label className="header-label">SECTION NO.</label>
              <input type="text" className="header-input" name="section_number"
                value={headerData.section_number} onChange={handleHeaderChange}
                placeholder="Enter Section No." />
            </div>
            <div className="header-field">
              <label className="header-label">DATE <span className="required-mark">*</span></label>
              <input type="date" className="header-input" name="date"
                value={headerData.date} onChange={handleHeaderChange} required />
            </div>
            <div className="header-field">
              <label className="header-label">CR DATE</label>
              <input type="date" className="header-input" name="cr_date"
                value={headerData.cr_date} onChange={handleHeaderChange} />
            </div>
            <div className="header-field">
              <label className="header-label">MARK DEPT.</label>
              <input type="text" className="header-input" name="mark_dept"
                value={headerData.mark_dept} onChange={handleHeaderChange}
                placeholder="Department" />
            </div>
          </div>

          {/* Row 2: Customer */}
          <div className="header-row">
            <div className="header-field full-width">
              <label className="header-label">CUSTOMER <span className="required-mark">*</span></label>
              <input type="text" className="header-input" name="customer"
                value={headerData.customer} onChange={handleHeaderChange}
                placeholder="Enter customer name" required />
            </div>
          </div>

          {/* Row 3: Project Name, Offer No, Offer Date */}
          <div className="header-row three-column">
            <div className="header-field wide">
              <label className="header-label">PROJECT NAME</label>
              <input type="text" className="header-input" name="project_name"
                value={headerData.project_name} onChange={handleHeaderChange}
                placeholder="Enter project name" />
            </div>
            <div className="header-field">
              <label className="header-label">OFFER NO.</label>
              <input type="text" className="header-input" name="offer_no"
                value={headerData.offer_no} onChange={handleHeaderChange}
                placeholder="Offer number" />
            </div>
            <div className="header-field narrow">
              <label className="header-label">DATE</label>
              <input type="date" className="header-input" name="offer_date"
                value={headerData.offer_date} onChange={handleHeaderChange} />
            </div>
          </div>

          {/* Row 4: PO No, PO Date, PO Received, TCDS Released */}
          <div className="header-row four-column">
            <div className="header-field">
              <label className="header-label">P.O. NO. <span className="required-mark">*</span></label>
              <input type="text" className="header-input" name="po_no"
                value={headerData.po_no} onChange={handleHeaderChange}
                placeholder="PO Number" required />
            </div>
            <div className="header-field">
              <label className="header-label">DATE</label>
              <input type="date" className="header-input" name="po_date"
                value={headerData.po_date} onChange={handleHeaderChange} />
            </div>
            <div className="header-field">
              <label className="header-label">P.O. RECEIVED ON</label>
              <input type="date" className="header-input" name="po_received_on"
                value={headerData.po_received_on} onChange={handleHeaderChange} />
            </div>
            <div className="header-field">
              <label className="header-label">TCDS RELEASED ON</label>
              <input type="date" className="header-input" name="tcds_released_on"
                value={headerData.tcds_released_on} onChange={handleHeaderChange} />
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            DYNAMIC SECTIONS
            ══════════════════════════════════════════ */}
        {sectionsLoading ? (
          <div className="sections-loading">
            <div className="loading-spinner"></div>
            <p>Loading form sections...</p>
          </div>
        ) : (
          <div className="api6d-sections">
            {sections.map((section) => (
              <SectionAccordion
                key={section.id}
                section={section}
                sectionData={sectionData[section.id] || {}}
                isExpanded={expandedSections[section.id]}
                onToggle={() => toggleSection(section.id)}
                onChange={(paramName, value) =>
                  handleSectionDataChange(section.id, paramName, value)
                }
              />
            ))}
          </div>
        )}

        {/* Form Actions */}
        <div className="api6d-form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate("/manage-tcds")}>
            Cancel
          </button>
          <button type="button" className="btn-save-draft"
            onClick={(e) => handleSubmit(e, true)} disabled={loading}>
            Save as Draft
          </button>
          <button type="button" className="btn-generate-pdf"
            onClick={handleGeneratePDF} disabled={loading}>
            Generate PDF
          </button>
          {/* ── NEW: Excel button ── */}
          <button type="button" className="btn-generate-excel"
            onClick={handleGenerateExcel} disabled={loading}>
            Generate Excel
          </button>
          <button type="submit" className="btn-submit" disabled={loading}>
            {editMode ? "Update TCDS" : "Submit TCDS"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section Accordion Component  (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────
function SectionAccordion({ section, sectionData, isExpanded, onToggle, onChange }) {
  const [parameters, setParameters] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isExpanded && parameters.length === 0) {
      loadSectionContent();
    }
  }, [isExpanded]);

  const loadSectionContent = async () => {
    try {
      setLoading(true);
      if (section.section_type === "STANDARD" || section.section_type === "API_SUBSECTION") {
        const response = await axios.get(`/tcds-sections/${section.id}/parameters?api_type=API6D`);
        if (response.data.success) {
          const paramsWithValues = await Promise.all(
            response.data.data.map(async (param) => {
              if (param.input_type === "DROPDOWN") {
                try {
                  const valuesResponse = await axios.get(
                    `/tcds-parameters/${param.id}/values?api_type=API6D`
                  );
                  return { ...param, values: valuesResponse.data.data || [] };
                } catch {
                  return { ...param, values: [] };
                }
              }
              return param;
            })
          );
          setParameters(paramsWithValues);
        }
      } else if (section.section_type === "VALUES_ONLY") {
        const response = await axios.get(`/tcds-sections/${section.id}/values`);
        if (response.data.success) setValues(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load section content:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionTypeColor = (type) => {
    switch (type) {
      case "STANDARD":      return "#1e3a8a";
      case "API_SUBSECTION": return "#7c3aed";
      case "VALUES_ONLY":   return "#059669";
      case "REVIEWER":      return "#dc2626";
      default:              return "#6b7280";
    }
  };

  const renderParameterInput = (param) => {
    const value = sectionData[param.parameter_name] || "";
    if (param.input_type === "DROPDOWN" && param.values?.length > 0) {
      return (
        <select className="parameter-select" value={value}
          onChange={(e) => onChange(param.parameter_name, e.target.value)}
          required={param.is_required}>
          <option value="">-- Select --</option>
          {param.values.map((v) => (
            <option key={v.id} value={v.value_text}>{v.value_text}</option>
          ))}
        </select>
      );
    } else if (param.input_type === "NUMBER") {
      return (
        <input type="number" className="parameter-input" value={value}
          onChange={(e) => onChange(param.parameter_name, e.target.value)}
          placeholder={`Enter ${param.parameter_name}`} required={param.is_required} />
      );
    }
    return (
      <input type="text" className="parameter-input" value={value}
        onChange={(e) => onChange(param.parameter_name, e.target.value)}
        placeholder={`Enter ${param.parameter_name}`} required={param.is_required} />
    );
  };

  const renderSectionContent = () => {
    if (loading) return <div className="section-content-loading">Loading...</div>;

    switch (section.section_type) {
      case "STANDARD":
      case "API_SUBSECTION":
        return (
          <div className="section-parameters">
            {parameters.length === 0 ? (
              <div className="no-parameters">No parameters configured for this section</div>
            ) : (
              <div className="parameters-grid">
                {parameters.map((param) => (
                  <div className="parameter-field" key={param.id}>
                    <label className="parameter-label">
                      {param.parameter_name}
                      {param.is_required && <span className="required-mark">*</span>}
                    </label>
                    {renderParameterInput(param)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case "VALUES_ONLY":
        return (
          <div className="section-values">
            {values.length === 0 ? (
              <div className="no-values">No values configured for this section</div>
            ) : (
              <ul className="values-checklist">
                {values.map((value, index) => (
                  <li key={value.id} className="value-item">
                    <span className="value-number">{index + 1}</span>
                    <span className="value-text">{value.value_text}</span>
                    <input type="checkbox" className="value-checkbox"
                      checked={sectionData[`value_${value.id}`] || false}
                      onChange={(e) => onChange(`value_${value.id}`, e.target.checked)} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      case "REVIEWER":
        return <ReviewerSection sectionData={sectionData} onChange={onChange} />;
      default:
        return <div>Unknown section type</div>;
    }
  };

  return (
    <div className="section-accordion">
      <div
        className={`section-accordion-header ${isExpanded ? "expanded" : ""}`}
        onClick={onToggle}
      >
        <div className="section-header-left">
          <span className="section-number">{section.section_number}</span>
          <span className="section-name">{section.section_name}</span>
        </div>
        <div className="section-header-right">
          <span className="section-type-badge" style={{
            backgroundColor: `${getSectionTypeColor(section.section_type)}15`,
            color: getSectionTypeColor(section.section_type)
          }}>
            {section.section_type.replace("_", " ")}
          </span>
          <span className="accordion-icon">{isExpanded ? "▼" : "▶"}</span>
        </div>
      </div>
      {isExpanded && (
        <div className="section-accordion-content">{renderSectionContent()}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reviewer Section Component  (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────
function ReviewerSection({ sectionData, onChange }) {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const departments = ["Planning", "Production", "Quality", "Purchase", "Sales", "Design"];

  useEffect(() => { fetchReviewers(); }, []);

  const fetchReviewers = async () => {
    try {
      const response = await axios.get("/tcds-reviewers");
      if (response.data.success) setReviewers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch reviewers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAmendmentChange = (index, field, value) => {
    const amendments = sectionData.amendments || [{}, {}, {}, {}, {}];
    const updatedAmendments = [...amendments];
    if (!updatedAmendments[index]) updatedAmendments[index] = {};
    updatedAmendments[index][field] = value;
    onChange("amendments", updatedAmendments);
  };

  const getAmendmentValue = (index, field) =>
    (sectionData.amendments || [])[index]?.[field] || "";

  if (loading) return <div className="reviewer-loading">Loading reviewers...</div>;

  return (
    <div className="reviewer-section">
      <div className="reviewer-grid">
        {departments.map((dept) => (
          <div className="reviewer-field" key={dept}>
            <label className="reviewer-label">{dept}</label>
            <select className="reviewer-select"
              value={sectionData[`reviewer_${dept.toLowerCase()}`] || ""}
              onChange={(e) => onChange(`reviewer_${dept.toLowerCase()}`, e.target.value)}>
              <option value="">-- Select Reviewer --</option>
              {reviewers.map((reviewer) => (
                <option key={reviewer.id} value={reviewer.id}>{reviewer.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="amendment-section">
        <h4>Amendment Description</h4>
        <table className="amendment-table">
          <thead>
            <tr>
              <th style={{ width: "5%" }}>Sr No</th>
              <th style={{ width: "35%" }}>Description of amendments sought</th>
              <th style={{ width: "12%" }}>Date</th>
              <th style={{ width: "30%" }}>Review Particulars</th>
              <th style={{ width: "12%" }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4].map((index) => (
              <tr key={index}>
                <td className="amendment-sr">{index + 1}</td>
                <td>
                  <input type="text" className="amendment-input"
                    value={getAmendmentValue(index, "description")}
                    onChange={(e) => handleAmendmentChange(index, "description", e.target.value)}
                    placeholder="Enter description..." />
                </td>
                <td>
                  <input type="date" className="amendment-date"
                    value={getAmendmentValue(index, "date")}
                    onChange={(e) => handleAmendmentChange(index, "date", e.target.value)} />
                </td>
                <td>
                  <input type="text" className="amendment-input"
                    value={getAmendmentValue(index, "review_particulars")}
                    onChange={(e) => handleAmendmentChange(index, "review_particulars", e.target.value)}
                    placeholder="Enter review particulars..." />
                </td>
                <td>
                  <input type="date" className="amendment-date"
                    value={getAmendmentValue(index, "review_date")}
                    onChange={(e) => handleAmendmentChange(index, "review_date", e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="approval-row">
          <div className="approval-field">
            <label>Reviewed By</label>
            <input type="text" className="approval-input"
              value={sectionData.reviewed_by || ""}
              onChange={(e) => onChange("reviewed_by", e.target.value)}
              placeholder="Enter reviewer name..." />
          </div>
          <div className="approval-field">
            <label>Approved By</label>
            <input type="text" className="approval-input"
              value={sectionData.approved_by || ""}
              onChange={(e) => onChange("approved_by", e.target.value)}
              placeholder="Enter approver name..." />
          </div>
        </div>
      </div>
    </div>
  );
}

export default API6DForm;